import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  ChevronDown,
  Search,
  Hash,
  ArrowUp,
} from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useAuth } from "@/context/auth-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { useTouchAgent } from "@/hooks/use-agent-prefs";
import { agentChannelsApi, type AgentChannel } from "@/api/agent-channels";
import { chatApi } from "@/api/chat";
import type { Agent } from "@/api/agents";
import { cn } from "@/lib/utils";
import { AgentAvatar } from "@/components/agent/agent-avatar";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { MobileHome } from "@/pages/mobile/MobileHome";

const LAST_AGENT_KEY = (tenantId: string) => `proclaw:lastAgent:${tenantId}`;

const DEFAULT_SUGGESTIONS = [
  "Summarise what happened today",
  "Draft a client update email",
  "Help me prep for my next meeting",
  "What should I be focusing on this week?",
];

/**
 * Greeting picker — hour-of-day → morning/afternoon/evening. Exported purely so
 * it can be exercised from a test when a runner gets added; not imported
 * elsewhere in the app today.
 */
export function timeOfDayGreeting(date: Date = new Date()): "morning" | "afternoon" | "evening" {
  const h = date.getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}

function firstName(user: { name: string | null; email: string } | null | undefined): string {
  if (!user) return "there";
  if (user.name) return user.name.split(/\s+/)[0];
  const emailPrefix = user.email?.split("@")[0] ?? "there";
  return emailPrefix.replace(/[._-]+/g, " ").split(/\s+/)[0] || "there";
}

export function TenantDashboard({
  onOpenWorkspace,
}: {
  onOpenWorkspace?: (ctx: { agentId: string; sessionId: string; channelId?: string }) => void;
} = {}) {
  useDocumentTitle("Dashboard");
  const isMobile = useIsMobile();
  if (isMobile) return <MobileHome />;
  return <TenantDashboardDesktop onOpenWorkspace={onOpenWorkspace} />;
}

function TenantDashboardDesktop({
  onOpenWorkspace,
}: {
  onOpenWorkspace?: (ctx: { agentId: string; sessionId: string; channelId?: string }) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const touchAgent = useTouchAgent();
  const { tenantId, tenantName } = useTenant();
  const { user } = useAuth();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [searchParams, setSearchParams] = useSearchParams();

  const greeting = timeOfDayGreeting();
  const name = firstName(user);

  // Selected agent — URL wins (so sidebar navigation can target a specific
  // agent), then last-used from localStorage, then the first available agent.
  const urlAgentId = searchParams.get("agentId");
  const urlChannelId = searchParams.get("channelId");
  useEffect(() => {
    if (urlChannelId !== "general") return;
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.delete("channelId");
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams, urlChannelId]);
  // Derived, never mirrored: the URL wins, then last-used from localStorage,
  // then the first accessible agent. Holding this in local state and
  // reconciling it with effects is what let the picker, the breadcrumb and
  // the URL drift apart mid-switch.
  const fallbackAgentId = useMemo(() => {
    if (agents.length === 0) return null;
    const stored =
      typeof window === "undefined" ? null : window.localStorage.getItem(LAST_AGENT_KEY(tenantId));
    return stored && agents.some((a) => a.id === stored) ? stored : agents[0].id;
  }, [agents, tenantId]);

  const selectedAgentId = useMemo(() => {
    // Trust the URL while the agent list is still loading; once it has
    // landed, an id that no longer resolves falls back rather than showing
    // an empty picker.
    if (urlAgentId && (agents.length === 0 || agents.some((a) => a.id === urlAgentId))) {
      return urlAgentId;
    }
    return fallbackAgentId;
  }, [urlAgentId, agents, fallbackAgentId]);

  useEffect(() => {
    if (!selectedAgentId || typeof window === "undefined") return;
    window.localStorage.setItem(LAST_AGENT_KEY(tenantId), selectedAgentId);
  }, [selectedAgentId, tenantId]);

  // Backfill the URL when it carries no usable agent, so the shared top-bar
  // chrome (the agent-settings gear) and deep links track the agent shown
  // here — the gear reads agentId from the URL only.
  //
  // This is the effect that, paired with the old mirrored `selectedAgentId`
  // state, produced the switcher ping-pong: state lagged the URL by one
  // render, so on every switch this fired with a stale closure and wrote the
  // PREVIOUS agent back over the fresh one via replaceState, which the sync
  // effect then copied back into state, forever.
  //
  // It is safe now only because `selectedAgentId` is derived from
  // `urlAgentId` above. That makes it provably convergent: when the URL
  // holds a valid agent the guard below is always true and nothing is
  // written at all; when it doesn't, exactly one write lands and the next
  // render satisfies the guard. Do not reintroduce a separate state copy.
  useEffect(() => {
    if (!selectedAgentId || urlAgentId === selectedAgentId) return;
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set("agentId", selectedAgentId);
        return params;
      },
      { replace: true },
    );
  }, [selectedAgentId, urlAgentId, setSearchParams]);

  const selectedAgent = agents.find((a) => a.id === selectedAgentId) ?? null;

  // Channels for the selected agent (for the channel selector). General is the
  // default when no channel id is set.
  const { data: channels = [] } = useQuery({
    queryKey: ["agent-channels", selectedAgentId],
    queryFn: () => agentChannelsApi.list(selectedAgentId!),
    enabled: !!selectedAgentId,
  });
  const [selectedChannelId, setSelectedChannelId] = useState<string | "general">(
    urlChannelId ?? "general",
  );
  useEffect(() => {
    // Reset channel when agent changes — channel lists are per-agent.
    setSelectedChannelId(urlChannelId ?? "general");
  }, [selectedAgentId, urlChannelId]);
  const selectAgent = useCallback(
    (agentId: string) => {
      if (agentId === urlAgentId) return;
      setSelectedChannelId("general");
      // Fire-and-forget; must not feed back into a URL write.
      touchAgent.mutate(agentId);
      // The single history entry for an agent switch. `selectedAgentId` is
      // derived from this URL, so there is nothing else to update.
      const params = new URLSearchParams({ agentId });
      navigate(`/tenants/${tenantId}/dashboard?${params.toString()}`);
    },
    [navigate, tenantId, touchAgent, urlAgentId],
  );

  const activeChannelName =
    selectedChannelId === "general"
      ? "General"
      : channels.find((c) => c.id === selectedChannelId)?.name ?? "General";

  useEffect(() => {
    if (selectedChannelId === "general" || channels.length === 0) return;
    if (!channels.some((channel) => channel.id === selectedChannelId)) {
      setSelectedChannelId("general");
    }
  }, [channels, selectedChannelId]);

  // Input + focus management
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-resize textarea
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 240) + "px";
  }, [input]);

  const hasAgents = agents.length > 0;
  const canSend = hasAgents && !!selectedAgentId && input.trim().length > 0 && !sending;

  const isMac =
    typeof navigator !== "undefined" &&
    (/Mac|iPhone|iPad|iPod/.test(navigator.platform || "") || /Mac OS X/.test(navigator.userAgent || ""));
  const sendHintModifier = isMac ? "⌘" : "Ctrl";

  const handleSend = useCallback(async () => {
    if (!canSend || !selectedAgentId) return;
    const content = input.trim();
    setSending(true);
    setSendError(null);
    try {
      const channelId = selectedChannelId === "general" ? undefined : selectedChannelId;
      const session = await chatApi.createSession(tenantId, selectedAgentId, channelId);
      // Prime the sessions cache so the sidebar & workspace see it immediately.
      queryClient.invalidateQueries({ queryKey: ["chat-sessions", selectedAgentId] });

      // Fire-and-forget the first message. We MUST hit the streaming endpoint
      // (?stream=true) — the non-streaming path on the server tries to JSON-
      // parse the agent gateway's SSE response and blows up with errors like
      // `Unexpected token ':' ": keep-ali"...`. The server already keeps the
      // agent running after client disconnect (commit ea520b8), so even
      // though we never read the stream, the assistant message is written to
      // the DB and the workspace picks it up via its message poll.
      fetch(`/api/chat/sessions/${session.id}/messages?stream=true`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
        keepalive: true,
      }).catch((err) => {
        console.error("Failed to send initial message:", err);
      });

      if (onOpenWorkspace) {
        onOpenWorkspace({
          agentId: selectedAgentId,
          sessionId: session.id,
          channelId,
        });
        return;
      }

      const params = new URLSearchParams({
        agentId: selectedAgentId,
        sessionId: session.id,
      });
      if (channelId) params.set("channelId", channelId);
      navigate(`/tenants/${tenantId}/workspace?${params.toString()}`);
    } catch (err) {
      console.error("Failed to start chat:", err);
      setSendError(err instanceof Error ? err.message : "Could not start chat.");
      setSending(false);
    }
  }, [canSend, selectedAgentId, input, selectedChannelId, tenantId, queryClient, navigate]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (e.shiftKey) return; // newline
      // Plain Enter OR ⌘/Ctrl+Enter both send
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="flex min-h-14 shrink-0 items-center border-b border-[#f0ede8] bg-white px-6 py-2 dark:border-border dark:bg-background lg:px-10">
        {/* Same header as the chat screen (chat-surface.tsx): avatar, agent
            name, then "# channel". */}
        <div className="flex min-w-0 items-center gap-2 text-left">
          {selectedAgent && (
            <AgentAvatar agent={selectedAgent} size={18} ringClassName="border-background" />
          )}
          <span className="truncate text-[14px] font-medium text-text-primary">
            {selectedAgent?.name ?? "No agent selected"}
          </span>
          <span className="truncate text-[12px] text-text-muted">
            # {activeChannelName}
          </span>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[720px] flex-1 flex-col justify-center px-4 py-10 sm:px-6">
        {/* Greeting */}
        <div className="mb-6 text-center">
          <h1 className="text-[26px] font-semibold text-text-primary sm:text-[30px]">
            Good {greeting}, {name}
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            What would you like to do today at {tenantName}?
          </p>
        </div>

        {/* Chat input card */}
        <div
          className={cn(
            "rounded-2xl border border-border bg-surface p-3 shadow-sm transition-shadow focus-within:border-accent/60 focus-within:shadow-md",
            !hasAgents && "opacity-70",
          )}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              hasAgents
                ? "Ask anything, or pick an agent below…"
                : "No agents available to you."
            }
            disabled={!hasAgents || sending}
            rows={2}
            className="w-full resize-none bg-transparent text-[15px] text-text-primary placeholder:text-text-muted focus:outline-none disabled:cursor-not-allowed"
            aria-label="Message"
          />

          {/* Toolbar */}
          <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border pt-2">
            {/* Agent picker pill */}
            <AgentPickerPill
              agents={agents}
              selectedAgentId={selectedAgentId}
              onSelect={selectAgent}
              disabled={!hasAgents}
            />

            {/* Channel selector — only when channels exist */}
            {selectedAgent && channels.length > 0 && (
              <ChannelPickerPill
                channels={channels}
                selectedChannelId={selectedChannelId}
                onSelect={setSelectedChannelId}
                activeChannelName={activeChannelName}
              />
            )}

            <div className="flex-1" />

            <span className="hidden text-[11px] text-text-muted sm:inline">
              {sendHintModifier} + Enter to send
            </span>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              aria-label="Send message"
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-full transition-colors",
                canSend
                  ? "bg-accent text-white hover:bg-accent-hover"
                  : "bg-surface-hover text-text-muted",
              )}
            >
              <ArrowUp size={14} />
            </button>
          </div>
        </div>

        {/* Empty / error state */}
        {!hasAgents && (
          <p className="mt-3 text-center text-xs text-text-muted sm:text-left">
            No agents available to you. Contact your tenant admin to request access.
          </p>
        )}
        {sendError && (
          <p className="mt-3 text-center text-xs text-red-500 sm:text-left">{sendError}</p>
        )}

        {/* Suggestion chips */}
        {hasAgents && DEFAULT_SUGGESTIONS.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
            {DEFAULT_SUGGESTIONS.slice(0, 4).map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => {
                  setInput(suggestion);
                  inputRef.current?.focus();
                }}
                className="rounded-full border border-border bg-surface px-3 py-1 text-[12.5px] text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Agent picker pill ─────────────────────────────────────────────────

interface AgentPickerPillProps {
  agents: Agent[];
  selectedAgentId: string | null;
  onSelect: (id: string) => void;
  disabled?: boolean;
}

function AgentRoleBadge({ role }: { role: string | null | undefined }) {
  if (!role) return null;
  return (
    <span className="shrink-0 rounded-full bg-surface-hover px-1.5 py-0.5 text-[9.5px] font-medium uppercase tracking-wide text-text-muted">
      {role}
    </span>
  );
}

function AgentStatusDot({ status }: { status: string | null | undefined }) {
  const isActive = status === "active" || status === "running";
  return (
    <span
      role="img"
      aria-label={isActive ? "Active" : "Idle"}
      className={cn(
        "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
        isActive ? "bg-success" : "bg-warning",
      )}
    />
  );
}

function AgentPickerPill({ agents, selectedAgentId, onSelect, disabled }: AgentPickerPillProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = "dashboard-agent-picker-list";

  const selected = agents.find((a) => a.id === selectedAgentId) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(
      (a) => a.name.toLowerCase().includes(q) || (a.role ?? "").toLowerCase().includes(q),
    );
  }, [agents, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  // Focus search on open; return focus to trigger on close
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    triggerRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (activeIndex >= filtered.length) {
      setActiveIndex(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, activeIndex]);

  const pick = (agent: Agent) => {
    onSelect(agent.id);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const a = filtered[activeIndex];
      if (a) pick(a);
      return;
    }
    if (e.key === "Tab") {
      // Keep focus inside the popover
      e.preventDefault();
      searchRef.current?.focus();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        className={cn(
          "flex items-center gap-1.5 rounded-full border border-border bg-surface px-2 py-1 text-[12.5px] text-text-secondary transition-colors",
          "hover:bg-surface-hover hover:text-text-primary",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
          "disabled:cursor-not-allowed disabled:opacity-60",
        )}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
          {selected?.avatarUrl ? (
            <img src={selected.avatarUrl} alt="" className="h-5 w-5 rounded-md object-cover" />
          ) : (
            <Bot size={11} />
          )}
        </span>
        <span className="max-w-[140px] truncate font-medium text-text-primary">
          {selected?.name ?? "Select agent"}
        </span>
        {selected?.role && <AgentRoleBadge role={selected.role} />}
        <ChevronDown
          size={11}
          className={cn(
            "shrink-0 text-text-muted motion-reduce:transition-none transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label="Agents available to you"
          aria-activedescendant={
            filtered[activeIndex] ? `dashboard-agent-opt-${filtered[activeIndex].id}` : undefined
          }
          onKeyDown={onKeyDown}
          className={cn(
            "absolute left-0 bottom-full z-30 mb-1 w-72 overflow-hidden rounded-xl border border-border bg-surface shadow-xl",
            // On narrow viewports anchor the popover to the full width of the
            // parent card instead of a fixed 288px box.
            "max-sm:left-0 max-sm:right-0 max-sm:w-auto",
          )}
        >
          <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
            <Search size={13} className="text-text-muted" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              placeholder="Search agents…"
              className="flex-1 bg-transparent text-xs text-text-primary placeholder:text-text-muted focus:outline-none"
              aria-label="Search agents"
            />
          </div>
          <div className="px-2 pt-2 pb-1">
            <p className="px-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
              Available to you
            </p>
          </div>
          <ul className="max-h-64 overflow-y-auto pb-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-center text-xs text-text-muted">
                {query.trim() ? "No matches." : "No agents available."}
              </li>
            ) : (
              filtered.map((a, i) => {
                const isActive = i === activeIndex;
                const isSelected = a.id === selectedAgentId;
                return (
                  <li key={a.id} role="option" id={`dashboard-agent-opt-${a.id}`} aria-selected={isSelected}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => pick(a)}
                      className={cn(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                        isActive
                          ? "bg-accent/10 text-accent"
                          : "text-text-secondary hover:bg-surface-hover",
                      )}
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
                        {a.avatarUrl ? (
                          <img src={a.avatarUrl} alt="" className="h-6 w-6 rounded-md object-cover" />
                        ) : (
                          <Bot size={12} />
                        )}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium">{a.name}</span>
                      <AgentStatusDot status={a.status} />
                      {a.role && <AgentRoleBadge role={a.role} />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Channel picker pill ───────────────────────────────────────────────

interface ChannelPickerPillProps {
  channels: AgentChannel[];
  selectedChannelId: string | "general";
  onSelect: (id: string | "general") => void;
  activeChannelName: string;
}

function ChannelPickerPill({
  channels,
  selectedChannelId,
  onSelect,
  activeChannelName,
}: ChannelPickerPillProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const pick = (id: string | "general") => {
    onSelect(id);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Channel: ${activeChannelName}`}
        className={cn(
          "flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-1 text-[12.5px] text-text-secondary transition-colors",
          "hover:bg-surface-hover hover:text-text-primary",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
        )}
      >
        <Hash size={11} className="text-text-muted" />
        <span className="max-w-[120px] truncate font-medium text-text-primary">
          {activeChannelName}
        </span>
        <ChevronDown
          size={11}
          className={cn(
            "shrink-0 text-text-muted motion-reduce:transition-none transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Channels"
          className="absolute left-0 bottom-full z-30 mb-1 w-56 overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
        >
          <ul className="max-h-60 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                role="option"
                aria-selected={selectedChannelId === "general"}
                onClick={() => pick("general")}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] transition-colors",
                  selectedChannelId === "general"
                    ? "bg-accent/10 text-accent"
                    : "text-text-secondary hover:bg-surface-hover",
                )}
              >
                <Hash size={12} />
                <span className="font-medium">General</span>
              </button>
            </li>
            {channels.map((ch) => {
              const isSelected = ch.id === selectedChannelId;
              return (
                <li key={ch.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => pick(ch.id)}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] transition-colors",
                      isSelected
                        ? "bg-accent/10 text-accent"
                        : "text-text-secondary hover:bg-surface-hover",
                    )}
                  >
                    <Hash size={12} />
                    <span className="min-w-0 flex-1 truncate font-medium">{ch.name}</span>
                    {ch.shared && (
                      <span className="shrink-0 text-[9.5px] uppercase tracking-wide text-text-muted">
                        shared
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
